import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

/**
 * What the cat's claws can hold: wood, and nothing else.
 *
 * The wall cling (player.js) works only against colliders tagged `climb`,
 * and this set is the whole of the tagging policy -- every path that creates
 * a collider routes its material through climbable() below, so "what counts
 * as wood" is decided here and nowhere else.  The set is material NAMES
 * because the fence runs never hand their Builder a material object: their
 * colliders come through collider()/raw() with the material passed alongside.
 *
 *   fence, fenceGrey   board fences, the level's main climbing surface
 *   timber             gates, the bench, the log store, the playhouse,
 *                      the signpost and bollard
 *   bark, barkDark     tree trunks
 *
 * trim is deliberately NOT here even though the painted front pickets are
 * made of it: trim is also the cream surround on every house window and the
 * porch columns, so the material cannot distinguish a picket from a house.
 * Painted wood opts in per piece with `climb: true` instead (see picketRun),
 * and a collider added bare -- no material, like the hidden box over a bay
 * window -- defaults to sheer.
 */
const CLIMB_MATS = new Set(["fence", "fenceGrey", "timber", "bark", "barkDark"]);

/** Does a surface of this material (or explicit boolean) take claws? */
export function climbable(grip) {
  if (typeof grip === "boolean") return grip;
  const name = grip == null ? null : grip.name ?? grip;
  return typeof name === "string" && CLIMB_MATS.has(name);
}

/**
 * Shared construction kit for the level modules.
 *
 * Each module gets one Builder and calls these helpers; the Builder collects
 * geometry per material and merges it at the end, so a street with thousands
 * of pieces still draws in a couple of dozen calls.
 *
 * Two conventions everything follows:
 *   - `y` is the BOTTOM of a piece, not its centre.  Placing things on the
 *     ground or on top of each other is then just addition.
 *   - Colliders are axis-aligned boxes, and are what the player actually
 *     touches.  Visual geometry may be rotated; its collider will not be, so
 *     anything the cat should stand on wants to be roughly box-shaped or to
 *     carry an explicit collider.
 */
export class Builder {
  constructor() {
    this.batches = new Map();     // material -> geometry[]
    this.colliders = [];
    this.markers = {};            // named points, for debugging and spawns
    // Geometry is merged per material for draw-call reasons, which throws away
    // which module drew what.  Keep a light index alongside it so the in-game
    // debug overlay can still name the thing under the crosshair.
    this.pieces = [];
    this.owner = "?";
    this.grade = null;
    this.updaters = [];           // per-frame callbacks, run by level.update
    this.clearKeys = new Map();   // material -> its transparent batch key
    this.surfaces = [];           // ground coverings, for surfaceY()
  }

  /**
   * Build everything from here on relative to the local ground.
   *
   * `g` is either a number (this whole module sits on one terrace) or a
   * function of (x, z) read at each piece's centre.  Pass a number for a
   * module that lives entirely on one side of a grade change: read per piece,
   * a deck board butted against the house would take the house's level while
   * the rest of the deck took the garden's, and the deck would come apart
   * along that seam.
   */
  setGrade(g) {
    this.grade = typeof g === "number" ? () => g : g;
    return this;
  }

  gradeAt(x, z) {
    return this.grade ? this.grade(x, z) : 0;
  }

  /** Tag everything built from here on as belonging to `name`. */
  setOwner(name) {
    this.owner = name;
    return this;
  }

  /**
   * Register a callback to run once per frame with the frame's delta time.
   *
   * The level is otherwise built once and never touched again, which is fine
   * for a street and not fine for water: something has to scroll the ripples.
   * A module registers here, finish() collects them, and main.js drives them
   * all through one level.update(dt).
   *
   * Do not close over anything the module built expecting to move it: the
   * geometry has been merged into a shared mesh by the time this runs, and
   * there is nothing left to address it by.  Animate materials, not pieces.
   */
  onUpdate(fn) {
    if (typeof fn === "function") this.updaters.push(fn);
    return this;
  }

  /**
   * The batch a transparent piece belongs in.
   *
   * finish() gives every batch one mesh with castShadow on, which is right for
   * a fence and wrong for water: a transparent surface in that pipeline stamps
   * a hard, entirely opaque shadow of itself onto the bed it is supposed to be
   * showing.  So transparent pieces key on a stand-in instead of on the
   * material, and come out as their own mesh with their own shadow and draw
   * settings.
   *
   * The stand-in carries `name` on purpose.  tools/inspect.mjs walks the batch
   * keys and prints `key.name` for the per-material breakdown; keyed on a bare
   * string or a symbol, every transparent batch in the level would report as
   * "?".
   */
  clearKey(material, renderOrder) {
    let k = this.clearKeys.get(material);
    if (!k) {
      k = { name: material.name, material, transparent: true, renderOrder };
      this.clearKeys.set(material, k);
    }
    return k;
  }

  // ---- raw ---------------------------------------------------------------
  /**
   * Add a geometry that is already positioned in world space.
   *
   * opts.collider: "auto" (bounding box), null/false (none), or an explicit
   * {x, y, z, w, h, d}.  opts.tile scales the world-projected UVs; pass 0 to
   * keep the geometry's own UVs (right for cylinders and cones, where a
   * planar projection would seam).
   *
   * opts.transparent: this piece is see-through.  It still grades, tiles,
   * shades and collides exactly as any other piece -- only which mesh it ends
   * up in changes.  opts.renderOrder (default 2) orders it within the
   * transparent pass, which runs after the whole opaque world.
   */
  add(geometry, material, opts = {}) {
    const { collider = null, tile = 0.55, tint = 0.11, ao = 0.28 } = opts;
    let g = geometry.index ? geometry.toNonIndexed() : geometry;
    let dy = 0;
    if (this.grade) {
      g.computeBoundingBox();
      const bb = g.boundingBox;
      dy = this.gradeAt((bb.min.x + bb.max.x) / 2, (bb.min.z + bb.max.z) / 2);
      if (dy) g.translate(0, dy, 0);
    }
    if (tile > 0) worldUV(g, tile);
    shade(g, tint, ao);
    const key = opts.transparent
      ? this.clearKey(material, opts.renderOrder ?? 2) : material;
    if (!this.batches.has(key)) this.batches.set(key, []);
    this.batches.get(key).push(g);

    g.computeBoundingBox();
    this.pieces.push({
      owner: this.owner,
      material: material.name || "?",
      box: g.boundingBox.clone(),
    });

    // A ground decal is something the cat should be standing ON, so remember
    // its footprint and its finished height.  Recorded here rather than in
    // slab() because `dy` has been applied by now: a surface has to be logged
    // at the level it ended up on, not the one it was written at.
    if (opts.surface) {
      const bb = g.boundingBox;
      if (opts.surface === true) {
        this.surfaces.push({ x0: bb.min.x, x1: bb.max.x,
                             z0: bb.min.z, z1: bb.max.z, top: bb.max.y });
      } else {
        // An oriented decal -- a paver yawed to a curve.  Its bounding box is
        // a poor stand-in for it: at 45 degrees the box of a 1.3 x 0.44 paver
        // is 1.23 square, so registering the box would lay an invisible ledge
        // most of a metre out into the lawn either side of the path.  The
        // caller hands us the rect it actually drew, and the query rotates
        // the point into it.
        const { x, z, w, d, yaw } = opts.surface;
        this.surfaces.push({
          cx: x, cz: z, hw: w / 2, hd: d / 2,
          cos: Math.cos(-yaw), sin: Math.sin(-yaw), top: bb.max.y,
          x0: bb.min.x, x1: bb.max.x, z0: bb.min.z, z1: bb.max.z,
        });
      }
    }

    if (collider === "auto") {
      g.computeBoundingBox();
      const b = g.boundingBox;
      this.tag(this.push(new THREE.Box3(b.min.clone(), b.max.clone())),
               opts.climb ?? material);
    } else if (collider && typeof collider === "object") {
      // `dy` is already in the geometry, so this goes in raw: grading it a
      // second time would leave the box a terrace below its own mesh.
      this.raw(collider.x, collider.y + dy, collider.z,
               collider.w, collider.h, collider.d, opts.climb ?? material);
    }
    return this;
  }

  /**
   * An invisible box the cat collides with, at the local ground level.
   *
   * `grip` is what the box's surface is made of -- a material, a name, or an
   * explicit boolean -- and feeds climbable().  Omitted means sheer: a bare
   * box has not said it is wood, so the claws refuse it.
   */
  collider(x, y, z, w, h, d, grip) {
    return this.raw(x, y + this.gradeAt(x, z), z, w, h, d, grip);
  }

  /** The same, in absolute world y -- for callers that did their own grading. */
  raw(x, y, z, w, h, d, grip) {
    this.tag(this.push(new THREE.Box3(
      new THREE.Vector3(x - w / 2, y, z - d / 2),
      new THREE.Vector3(x + w / 2, y + h, z + d / 2))), grip);
    return this;
  }

  push(box) {
    this.colliders.push(box);
    return box;
  }

  /**
   * Stamp a collider with what the claws need to know.  `owner` and `mat`
   * ride along for the tools: climb.mjs audits the tagging by material, and
   * an untagged tally would leave "did the houses get swept in" unanswerable.
   */
  tag(box, grip) {
    box.climb = climbable(grip);
    box.owner = this.owner;
    box.mat = grip == null || typeof grip === "boolean"
      ? null : grip.name ?? grip;
    return box;
  }

  // ---- primitives --------------------------------------------------------
  /** A solid box. `y` is its underside. */
  block(x, y, z, w, h, d, material, opts = {}) {
    const g = new THREE.BoxGeometry(w, h, d);
    g.translate(x, y + h / 2, z);
    return this.add(g, material, {
      collider: opts.solid === false ? null : { x, y, z, w, h, d },
      ...opts,
    });
  }

  /**
   * A flat ground decal: paving, tarmac, a lawn.  No collider -- but it does
   * register as a SURFACE, which is how the cat comes to stand on it.
   *
   * Nothing here is thick enough to be worth a collider, and giving hundreds
   * of 2 cm boxes one would cost the broadphase far more than it is worth.
   * But they are not nothing either: a sod roll is 25 mm and paving is laid
   * proud of the grass it is set into, so a cat floored on the bare earth
   * stands with its paws a visible few centimetres inside the patio.  See
   * level/index.js surfaceY, which is what actually resolves this.
   *
   * Pass `surface: false` for a slab that is decoration rather than floor.
   */
  slab(x, y, z, w, d, material, opts = {}) {
    const h = opts.thickness ?? 0.02;
    const g = new THREE.BoxGeometry(w, h, d);
    g.translate(x, y + h / 2, z);
    return this.add(g, material,
      { collider: null, ao: 0, tile: opts.tile ?? 1.2, surface: true, ...opts });
  }

  /** An upright cylinder: post, trunk, bin, bollard. */
  post(x, y, z, r, h, material, opts = {}) {
    const seg = opts.segments ?? 10;
    const g = new THREE.CylinderGeometry((opts.topRadius ?? r) * 1.0, r, h, seg);
    g.translate(x, y + h / 2, z);
    return this.add(g, material, {
      tile: 0,
      collider: opts.solid === false ? null
        : { x, y, z, w: r * 1.7, h, d: r * 1.7 },
      ...opts,
    });
  }

  /** A cylinder lying along "x" or "z": a fallen log, a pipe, a rail. */
  bar(x, y, z, r, len, axis, material, opts = {}) {
    const g = new THREE.CylinderGeometry(r, r, len, opts.segments ?? 10);
    g.rotateZ(Math.PI / 2);
    if (axis === "z") g.rotateY(Math.PI / 2);
    g.translate(x, y + r, z);
    const c = axis === "x"
      ? { x, y, z, w: len, h: r * 2, d: r * 1.7 }
      : { x, y, z, w: r * 1.7, h: r * 2, d: len };
    return this.add(g, material, {
      tile: 0, collider: opts.solid === false ? null : c, ...opts,
    });
  }

  cone(x, y, z, r, h, material, opts = {}) {
    const g = new THREE.ConeGeometry(r, h, opts.segments ?? 9);
    g.translate(x, y + h / 2, z);
    return this.add(g, material, { tile: 0, collider: null, ...opts });
  }

  sphere(x, y, z, r, material, opts = {}) {
    const g = new THREE.SphereGeometry(r, opts.segments ?? 10, opts.rings ?? 7);
    if (opts.squash) g.scale(1, opts.squash, 1);
    g.translate(x, y + r * (opts.squash ?? 1), z);
    return this.add(g, material, { tile: 0, collider: null, ...opts });
  }

  /** A faceted boulder. */
  rock(x, y, z, r, material, opts = {}) {
    const g = new THREE.IcosahedronGeometry(r, opts.detail ?? 0);
    g.scale(1, 0.72, 0.9);
    g.rotateY(x * 1.7 + z);
    g.translate(x, y + r * 0.52, z);
    return this.add(g, material, {
      collider: { x, y, z, w: r * 1.70, h: r * 1.13, d: r * 1.55 }, ...opts,
    });
  }

  /**
   * A gable roof: a triangular prism ridged along "x" or "z".
   *
   * The collider is a flat box at the eaves rather than the true slope, so a
   * cat that gets onto a roof stands on it instead of sliding through. Pass
   * `solid: false` for roofs that should not be reachable at all.
   */
  roof(x, y, z, w, d, h, material, opts = {}) {
    const ridge = opts.ridge ?? "x";
    const len = ridge === "x" ? w : d;
    const span = ridge === "x" ? d : w;
    const half = span / 2;
    const pos = [], idx = [];
    // profile: two eaves and a ridge, extruded along the ridge axis
    const prof = [[-half, 0], [half, 0], [0, h]];
    for (const s of [-len / 2, len / 2]) {
      for (const [a, up] of prof) {
        pos.push(ridge === "x" ? [s, up, a] : [a, up, s]);
      }
    }
    // Swapping the extrusion axis reverses the handedness, so the two
    // orientations need their own winding -- sharing one index list leaves the
    // gable ends facing inward and the prism renders hollow under backface
    // culling.
    const quad = (a, bb, c, dd) => idx.push(a, bb, c, a, c, dd);
    if (ridge === "x") {
      idx.push(0, 1, 2, 5, 4, 3);                     // gable ends
      quad(0, 3, 4, 1);                               // underside
      quad(1, 4, 5, 2);                               // slope one way
      quad(2, 5, 3, 0);                               // slope the other
    } else {
      idx.push(2, 1, 0, 3, 4, 5);
      quad(0, 1, 4, 3);
      quad(2, 5, 4, 1);
      quad(0, 3, 5, 2);
    }
    let g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos.flat(), 3));
    g.setIndex(idx);
    g = g.toNonIndexed();
    g.computeVertexNormals();
    g.translate(x, y, z);
    return this.add(g, material, {
      collider: opts.solid === false ? null
        : { x, y, z, w, h: Math.max(h * 0.55, 0.4), d },
      ...opts,
    });
  }

  /** Remember a point of interest (spawn, gate, route hint). */
  mark(name, x, y, z) {
    this.markers[name] = { x, y: y + this.gradeAt(x, z), z };
    return this;
  }

  // ---- output ------------------------------------------------------------
  /** Merge everything and attach it to the scene. */
  finish(scene) {
    let tris = 0;
    for (const [key, geos] of this.batches) {
      if (!geos.length) continue;
      const material = key.material ?? key;      // transparent batches key on a stand-in
      const merged = mergeGeometries(geos, false);
      if (!merged) {
        console.warn("level: could not merge batch for", material.name);
        continue;
      }
      tris += merged.attributes.position.count / 3;
      const mesh = new THREE.Mesh(merged, material);
      // Still receives: a creek under trees wants their shadows on it.  It is
      // only casting that has to go.
      mesh.castShadow = !key.transparent;
      mesh.receiveShadow = true;
      if (key.transparent) mesh.renderOrder = key.renderOrder;
      scene.add(mesh);
    }

    // Snapshotted rather than read from `this` each frame, so a module that
    // registers after the level is built cannot start animating geometry that
    // was never merged into the scene.
    const fns = this.updaters.slice();
    const update = (dt) => {
      for (let i = 0; i < fns.length; i++) {
        try {
          fns[i](dt);
        } catch (e) {
          // frame() has already queued the next animation frame before it gets
          // here, so a thrower does not stop the loop -- it just skips the
          // render, silently, for the rest of the session.  Drop it and keep
          // the game, the same bargain buildLevel makes for a failed module.
          console.error("level: update callback failed, dropping it --", e);
          fns.splice(i--, 1);
        }
      }
    };

    // Deliberately does NOT report a `colliders` count: callers spread this
    // alongside the collider *array*, and a same-named number silently
    // replaced it -- which surfaces far away as "forEach is not a function".
    return { triangles: tris | 0, draws: this.batches.size, update };
  }

  /** Counts without needing a scene -- used by the headless inspector. */
  stats() {
    let tris = 0;
    const bb = new THREE.Box3();
    for (const geos of this.batches.values()) {
      for (const g of geos) {
        tris += g.attributes.position.count / 3;
        g.computeBoundingBox();
        bb.union(g.boundingBox);
      }
    }
    return { triangles: tris | 0, batches: this.batches.size,
             colliders: this.colliders.length, bounds: bb };
  }
}

// ---------------------------------------------------------------------------
/**
 * Project UVs from world position so texel density matches everywhere.
 *
 * A BoxGeometry maps 0..1 across each face whatever its size, so one brick
 * tile would stretch across a whole house wall and repeat six times on a
 * doorstep.  Projecting from the two world axes each face is most aligned with
 * fixes the scale for every box at once.
 */
export function worldUV(g, tile = 0.55) {
  const pos = g.attributes.position;
  const nrm = g.attributes.normal;
  if (!nrm) g.computeVertexNormals();
  const n = g.attributes.normal;
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    const nx = Math.abs(n.getX(i)), ny = Math.abs(n.getY(i)), nz = Math.abs(n.getZ(i));
    let u, v;
    if (ny >= nx && ny >= nz) { u = pos.getX(i); v = pos.getZ(i); }
    else if (nx >= nz) { u = pos.getZ(i); v = pos.getY(i); }
    else { u = pos.getX(i); v = pos.getY(i); }
    uv[i * 2] = u / tile;
    uv[i * 2 + 1] = v / tile;
  }
  g.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  return g;
}

let seed = 0x9e3779b9;
function rnd() {
  seed ^= seed << 13; seed ^= seed >>> 17; seed ^= seed << 5;
  return ((seed >>> 0) % 100000) / 100000;
}

/**
 * Per-piece tint plus a darkening toward its base.
 *
 * Carries two things the lighting cannot: a little variation so a row of
 * identical fence panels is not one flat slab, and contact shading at the
 * ground that the shadow map is far too coarse to give.
 */
export function shade(g, spread = 0.11, ao = 0.28) {
  const pos = g.attributes.position;
  const k0 = 1 - spread * 0.5 + rnd() * spread;
  g.computeBoundingBox();
  const lo = g.boundingBox.min.y;
  const span = Math.max(g.boundingBox.max.y - lo, 1e-4);
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const h = (pos.getY(i) - lo) / span;
    const k = k0 * (1 - ao * (1 - Math.min(1, h * 1.6)));
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = k;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return g;
}

/** Deterministic per-call random, so a rebuilt level is identical. */
export function makeRng(s) {
  let x = s >>> 0 || 1;
  return () => {
    x ^= x << 13; x >>>= 0; x ^= x >>> 17; x ^= x << 5; x >>>= 0;
    return x / 4294967296;
  };
}
